import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Settings, Globe, Shield, Bell, Save,
  Building2, Key, Database, Cloud
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import IntegrationsStatusPanel from './IntegrationsStatusPanel';
import { SUPPORT_PHONE } from '@/config/support';

export default function CompanySettingsPage() {
  const { token, admin } = useOutletContext();
  
  const [platformSettings, setPlatformSettings] = useState({
    platform_name: 'Chain29',
    support_email: 'support@asterpos.com',
    support_phone: SUPPORT_PHONE,
    default_currency: 'USD',
    default_timezone: 'America/New_York'
  });
  
  const [securitySettings, setSecuritySettings] = useState({
    require_2fa: true,
    session_timeout: 30,
    password_min_length: 8,
    max_login_attempts: 5,
    require_captcha: true
  });
  
  const [notificationSettings, setNotificationSettings] = useState({
    new_restaurant_alert: true,
    payment_failure_alert: true,
    daily_summary: true,
    weekly_report: true
  });

  // There is no backend for these settings yet: nothing on this page is saved.

  return (
    <div className="space-y-6" data-testid="company-settings">
      <div>
        <h1 className="text-2xl font-bold text-white">Platform Settings</h1>
        <p className="text-slate-400">Configure global platform settings</p>
      </div>

      <Tabs defaultValue="platform" className="space-y-6">
        <TabsList className="bg-slate-800 border border-slate-700">
          <TabsTrigger value="platform" className="data-[state=active]:bg-slate-700 text-slate-300">
            <Globe className="h-4 w-4 mr-2" />
            Platform
          </TabsTrigger>
          <TabsTrigger value="security" className="data-[state=active]:bg-slate-700 text-slate-300">
            <Shield className="h-4 w-4 mr-2" />
            Security
          </TabsTrigger>
          <TabsTrigger value="notifications" className="data-[state=active]:bg-slate-700 text-slate-300">
            <Bell className="h-4 w-4 mr-2" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="integrations" className="data-[state=active]:bg-slate-700 text-slate-300">
            <Cloud className="h-4 w-4 mr-2" />
            Integrations
          </TabsTrigger>
        </TabsList>

        {/* Platform Settings */}
        <TabsContent value="platform">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Platform Configuration</CardTitle>
              <CardDescription className="text-slate-400">Basic platform settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Platform Name</Label>
                  <Input
                    value={platformSettings.platform_name}
                    onChange={(e) => setPlatformSettings({...platformSettings, platform_name: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Support Email</Label>
                  <Input
                    value={platformSettings.support_email}
                    onChange={(e) => setPlatformSettings({...platformSettings, support_email: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Support Phone</Label>
                  <Input
                    value={platformSettings.support_phone}
                    onChange={(e) => setPlatformSettings({...platformSettings, support_phone: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Default Currency</Label>
                  <Input
                    value={platformSettings.default_currency}
                    onChange={(e) => setPlatformSettings({...platformSettings, default_currency: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <p className="text-sm text-slate-400">Platform settings are managed by the platform admin</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Settings */}
        <TabsContent value="security">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Security Settings</CardTitle>
              <CardDescription className="text-slate-400">Platform security configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-700 rounded-lg">
                <div>
                  <p className="font-medium text-white">Require Two-Factor Authentication</p>
                  <p className="text-sm text-slate-400">Enforce 2FA for all admin accounts</p>
                </div>
                <Switch
                  checked={securitySettings.require_2fa}
                  onCheckedChange={(checked) => setSecuritySettings({...securitySettings, require_2fa: checked})}
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-700 rounded-lg">
                <div>
                  <p className="font-medium text-white">Require CAPTCHA on Login</p>
                  <p className="text-sm text-slate-400">Protect against automated attacks</p>
                </div>
                <Switch
                  checked={securitySettings.require_captcha}
                  onCheckedChange={(checked) => setSecuritySettings({...securitySettings, require_captcha: checked})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Session Timeout (minutes)</Label>
                  <Input
                    type="number"
                    value={securitySettings.session_timeout}
                    onChange={(e) => setSecuritySettings({...securitySettings, session_timeout: parseInt(e.target.value)})}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Max Login Attempts</Label>
                  <Input
                    type="number"
                    value={securitySettings.max_login_attempts}
                    onChange={(e) => setSecuritySettings({...securitySettings, max_login_attempts: parseInt(e.target.value)})}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <p className="text-sm text-slate-400">Platform settings are managed by the platform admin</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notification Settings */}
        <TabsContent value="notifications">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Notification Preferences</CardTitle>
              <CardDescription className="text-slate-400">Configure admin alerts</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: 'new_restaurant_alert', label: 'New Restaurant Signups', desc: 'Alert when new restaurants join' },
                { key: 'payment_failure_alert', label: 'Payment Failures', desc: 'Alert on payment processing issues' },
                { key: 'daily_summary', label: 'Daily Summary', desc: 'Receive daily platform summary' },
                { key: 'weekly_report', label: 'Weekly Report', desc: 'Comprehensive weekly analytics' }
              ].map(item => (
                <div key={item.key} className="flex items-center justify-between p-4 bg-slate-700 rounded-lg">
                  <div>
                    <p className="font-medium text-white">{item.label}</p>
                    <p className="text-sm text-slate-400">{item.desc}</p>
                  </div>
                  <Switch
                    checked={notificationSettings[item.key]}
                    onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, [item.key]: checked})}
                  />
                </div>
              ))}
              <div className="flex justify-end">
                <p className="text-sm text-slate-400">Platform settings are managed by the platform admin</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations: live checks from the backend, never a hard-coded "connected" */}
        <TabsContent value="integrations">
          <IntegrationsStatusPanel token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
